import { params } from '../modules/events/movePages';
import { apiClient } from '../utils/api';

/** Cache signed params so reschedule/cancel still work if the URL is changed (e.g. navigation or iframe). */
let cachedSignedParams = null;

export async function createEvent(appointmentChannel, payload) {
    return await apiClient.POST(`api/widget/events/appointment-channel/${appointmentChannel}`, payload);
}
export async function confirmEvent(eventId, appointmentChannel) {
    return await apiClient.GET(`api/events/${eventId}/appointment-channel/${appointmentChannel}/confirmation`);
}
export async function updateEvevntStatus(eventId) {
    return await apiClient.PUT(`api/widget/events/${eventId}/confirm`);
}

/**
 * Create a SetupIntent for saving a card. Backend creates it on the correct Stripe account
 * (platform or Connect), so when we confirm with the client_secret the PaymentMethod
 * is created in that account—no need for the connected account's publishable key.
 */
export async function createEventPaymentMethodSetupIntent(eventId) {
    const res = await apiClient.POST(`api/widget/events/${eventId}/payment-method/setup-intent`, {});
    return res?.data?.client_secret ?? null;
}

export async function saveEventPaymentMethod(eventId, paymentMethodId) {
    return await apiClient.PUT(`api/widget/events/${eventId}/payment-method`, {
        payment_method_id: paymentMethodId,
    });
}

export async function getEvevntInfoById(eventId) {
    return await apiClient.GET(`api/events/${eventId}`);
}

/**
 * Load event for drop-off resume link (no auth; validated by signature in URL).
 * Use when event_id + dropoff_resume=summary + signature + expires are in the page URL.
 */
export async function getEventResume(eventId, signature, expires) {
    const query = new URLSearchParams({ signature, expires });
    return await apiClient.GET(`api/widget/events/${eventId}/resume?${query.toString()}`);
}

/**
 * Get a short-lived token for the lead so the widget can auto-login from OTP drop-off link.
 * Use when dropoff_resume=after_otp + lead_id + signature + expires are in the page URL.
 */
export async function getLeadResumeToken(leadId, signature, expires) {
    const query = new URLSearchParams({ signature, expires });
    return await apiClient.GET(`api/widget/leads/${leadId}/resume-token?${query.toString()}`);
}

export async function cancelAppointment(eventId, reason) {
    const signedParams = getSignedMiddlewareParams();
    const url = `api/events/${eventId}/action${signedParams}`;


    return await apiClient.PUT(url, { action: 'cancel', reason });
}

export async function cancelEvent(eventId, reason) {
    const signedParams = getSignedMiddlewareParams();
    const url = `api/events/${eventId}/cancel${signedParams}`;

    return await apiClient.PUT(url, { reason });
}

export async function confirmAppointment(eventId, payload) {
    const signedParams = getSignedMiddlewareParams();
    const url = `api/events/${eventId}/action${signedParams}`;
    return await apiClient.PUT(url, {
        ...payload,
        action: 'confirm'
    });
}

export async function rescheduleAppointment(eventId, payload) {
    const signedParams = getSignedMiddlewareParams();
    const url = `api/events/${eventId}/reschedule${signedParams}`;

    return await apiClient.PUT(url, payload);
}

/** Call when the reschedule/cancel flow loads so we cache signature/expires/v from the URL for the later API call. */
export function primeRescheduleSignatureParams() {
    getSignedMiddlewareParams();
}

function getParamFromLocation(param, location) {
    if (!location || !location.search) return null;
    const search = (location.search || '').replace(/^\?+/, '?');
    const urlParams = new URLSearchParams(search);
    return urlParams.get(param);
}

function getSignedMiddlewareParams() {
    let signature = params('signature');
    let expires = params('expires');
    let version = params('v');

    if ((!signature || !expires) && window.self !== window.top) {
        try {
            signature = signature || getParamFromLocation('signature', window.top.location);
            expires = expires || getParamFromLocation('expires', window.top.location);
            version = version || getParamFromLocation('v', window.top.location);
        } catch (_) {
            // Cross-origin: can't read top.location
        }
    }

    if (signature && expires) {
        cachedSignedParams = { signature, expires, version };
    } else if (cachedSignedParams) {
        signature = cachedSignedParams.signature;
        expires = cachedSignedParams.expires;
        version = cachedSignedParams.version;
    }

    let queryParams = '';
    if (signature && expires) {
        // Laravel signs the URL with params in alphabetical order (ksort). Validation uses raw QUERY_STRING,
        // so we must send params in the same order: expires, signature, v.
        const parts = [`expires=${expires}`, `signature=${signature}`];
        if (version != null && version !== '') {
            parts.push(`v=${version}`);
        }
        queryParams = '?' + parts.join('&');
    }

    return queryParams;
}
